import { Fragment, useCallback, useEffect, useRef, useState } from "react";

import { attempt } from "~/api/attempt";
import { act, type Settle } from "~/beacon";
import { NeedsSignIn, api, call } from "~/api/client";
import type {
  BriefedRequest,
  BriefedResponse,
  BriefingResponse,
  CalendarEntry,
  Leftover,
} from "~/api/types";

import s from "./Briefing.module.css";
import { heldByStar, onList, whoIs } from "~/calendar/views";
import { useWaiting, waitingChanged } from "~/social/waiting";
import { EventCard } from "~/components/EventCard";
import { Faces } from "~/components/Faces";
import type { MarksActions } from "~/components/MarksContext";
import { MarksProvider } from "~/components/MarksContext";
import { Bookmark } from '~/components/Bookmark'
import { Star } from '~/components/Star'

/**
 * The briefing, ported from `_calendar_page.py` — the `// ── the briefing ──` block and
 * `openBriefing` under it.
 *
 * The queue of undecided gigs, one screen at a time. A screen and not a dialog, which is the
 * Python's own decision: "A modal made a decision queue feel like an interruption to get
 * past; as a page it is somewhere you go, and everything you already know how to do to a gig
 * works there." So there is no scrim, no `role="dialog"`, no focus trap and no Escape
 * handler here — the Python emits none of those, and adding them would be a redesign.
 *
 * There are no per-row No buttons. Hearting or ticking is the yes, and Done says no to
 * whatever is left — so the answer for most rows is to do nothing, which is the answer most
 * rows deserve.
 */

/**
 * The signed-in viewer.
 *
 * Not nullable, and that is the load-bearing part: the briefing is the one read that needs
 * an account. Fetching it signed out returned a 401 whose body was then handed to `.json()`,
 * which threw — so a quarter of the mobile navigation did nothing at all and never offered
 * the sign-in it was asking for. The button that opens this is hidden from a signed-out
 * reader, and the type says the same thing a second time.
 */
export interface Me {
  id: number;
  name: string;
  username: string;
  isEditor: boolean;
}

/* ── the page size ─────────────────────────────────────────────────────────────────── */

/**
 * The sizes offered, and the one in force. The server clamps whatever arrives to 6–200, so
 * these are a menu rather than the rule.
 */
const PAGE_SIZES = [24, 50, 100, 200] as const;

/** How a row is drawn: the compact line, or the card the rest of the site uses. */
const STYLES = [
  ["compact", "Rows"],
  ["full", "Cards"],
] as const;
type Style = (typeof STYLES)[number][0];
const STYLE_KEY = "lg.brief.style";

/**
 * Kept on the device rather than the account, for the same reason the page size is: it is a
 * fact about the screen you are reading on. Cards are a lot of scrolling on a phone and the
 * obvious choice on a monitor.
 */

/**
 * Whether the click was the row itself rather than a link inside it. Carried here as well as
 * in `ListView`, and for the same reason: a tap on a link navigated twice — the anchor and
 * the row, to the same address — and that second history entry is what left the sheet mounted
 * with its dialog closed, so every later tap did nothing until the page was reloaded.
 */
function openedTheRow(ev: React.MouseEvent): boolean {
  return !(ev.target as HTMLElement).closest('a,button')
}

function briefStyle(): Style {
  try {
    const saved = window.localStorage.getItem(STYLE_KEY);
    return STYLES.some(([key]) => key === saved) ? (saved as Style) : "compact";
  } catch {
    return "compact";
  }
}

function rememberStyle(style: Style): void {
  try {
    window.localStorage.setItem(STYLE_KEY, style);
  } catch {
    // A preference is not worth a visible failure; the default is a fine answer.
  }
}

const STORED = "briefPerPage";

/**
 * `localStorage` throws outright in a few privacy modes rather than merely failing to store,
 * so *reading* it is guarded and not only writing. Safari with cross-site tracking blocked
 * and Firefox with cookies denied both raise a `SecurityError` on the getter, which without
 * this takes the whole briefing down before it has asked for a row.
 */
export function perPage(): number {
  try {
    const saved = Number(localStorage.getItem(STORED));
    return (PAGE_SIZES as readonly number[]).includes(saved)
      ? saved
      : PAGE_SIZES[0];
  } catch {
    return PAGE_SIZES[0];
  }
}

function rememberPerPage(size: number): void {
  try {
    localStorage.setItem(STORED, String(size));
  } catch {
    /* Not fatal. The size is a fact about the screen, not about the account — losing it
       costs a reader one click next time and nothing else. */
  }
}

/* ── the two calls `api` does not carry ────────────────────────────────────────────── */

/*
 * `GET /briefing` and `POST /briefed`, through `~/api/client`'s own `call`. This file once
 * carried a copy of it, written while the port could not edit that one, and named as a seam
 * to close: one copy is one policy for credentials, a 401 and a page where JSON was expected,
 * and the one place the beacon counts the requests still out.
 */

/**
 * Always page 0. There is no page cursor any more, and that is the fix rather than a
 * simplification: paging by offset over a list that *shrinks* as you answer re-shows rows.
 * Heart two on page one, ask for page two, and it starts two events earlier than it should —
 * so two events you had already scrolled past come back. Done clears the page and the next
 * twenty-four become page 0.
 */
function getBriefing(per: number): Promise<BriefingResponse> {
  return call<BriefingResponse>(`/briefing?page=0&per=${per}`);
}

function postBriefed(body: BriefedRequest): Promise<BriefedResponse> {
  return call<BriefedResponse>("/briefed", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/* ── the row ───────────────────────────────────────────────────────────────────────── */

/** The price, in pounds. Decimals only when there are any. */
function money(e: CalendarEntry): string {
  if (e.priceMin == null) return "";
  const f = (n: number) => "£" + (n % 1 ? n.toFixed(2) : n);
  return e.priceMax && e.priceMax !== e.priceMin
    ? `${f(e.priceMin / 100)}–${f(e.priceMax / 100)}`
    : f(e.priceMin / 100);
}

/** Cut to length, at a bill separator where there is one, so the result reads as names
 *  rather than as a word sliced in half. */
function short(value: string | null, n: number): string {
  const text = String(value ?? "");
  if (text.length <= n) return text;
  const cut = text.slice(0, n);
  const at = cut.lastIndexOf(" + ");
  return (at > n / 2 ? cut.slice(0, at) : cut.trimEnd()) + " …";
}

/**
 * What the heart means, in one place. A gig is on the list two ways that are not a personal
 * save: the editor added it, or a starred act headlines it. Both are `picked`.
 */

/** Whether the star is what is holding it there. When it is, the heart cannot take it off —
 *  unstarring is what does that — so the row shows the star instead. */
/**
 * Whether the mark on this row is *yours* — which is exactly what pressing it changes.
 *
 * **A control has to show the thing it toggles.** This returned `onList`, which is
 * `picked || liked` for the editor, and `picked` is `curation_status in (added, highlight)
 * *or* starred. So on a gig headlined by an act you follow the mark drew full because of the
 * star, while the press acted on your own save — and `api.save(id, false)` un-curated a gig
 * the star put straight back. The row emptied optimistically and was full again on reload;
 * the server was never left holding anything. Measured on the live pile: 21 of the 24 rows
 * on page one are star-held, because a followed act is worth 100 in the ranking and sorts
 * every one of them to the top. So the first page of the briefing was a page of marks that
 * did nothing, which is how it was reported.
 *
 * It read `onList` deliberately — "the heart is a state, not an answer", after a spell where
 * these rows drew empty. That reading is defensible and it is not the one this control can
 * afford: a mark you cannot press is worse than a mark that says less. The star beside it
 * still says the gig is on your list and still takes it off, which is the fact `onList` was
 * there to carry.
 *
 * For the editor a save and a place on the published calendar are one fact rather than two —
 * `people.save_event` writes both, on purpose — so either proves they pressed it.
 */
function mine(e: CalendarEntry, me: Me | null): boolean {
  if (!me?.isEditor) return Boolean(e.liked);
  return Boolean(e.liked || e.status === "added" || e.status === "highlight");
}

/**
 * Whether this row has been dealt with — which is *not* whether its heart is full.
 *
 * The same two facts the server's pile excludes on, and it has to be exactly those two.
 * `briefing.py` builds the pile from `not liked and not going and not dismissed`; this
 * decides which rows `Done` acknowledges. Where the two disagree, a row the page calls
 * answered is a row nothing ever dismisses and the server keeps handing back — which is a
 * queue that cannot be advanced past its first page.
 *
 * That is what reading `onList` here did. It is `picked || liked` for the editor, and
 * `picked` is `curation_status in (added, highlight) or starred` — so 264 gigs the editor
 * had added, and every gig by an act they follow, counted as answered, were left out of the
 * dismiss list, and came back on the next visit. Pressing Done returned the same twenty-four
 * rows with the same count beside them.
 *
 * The heart is deliberately not consulted. It says what is on your list, which is a state;
 * this says whether you have looked at this night, which is the only question the page asks.
 * A gig can honestly be on the list and unanswered — that is most of them.
 */
function answered(e: CalendarEntry, me: Me | null): boolean {
  void me;
  return Boolean(e.going || e.liked);
}

/*
 * Drawn, not typed. `♥` is a text glyph: at the 12px these sit at the font hints it into a
 * wedge, and which wedge depends on the platform's emoji font. A path is the same shape at
 * every size and on every machine.
 */

function Tick({ fill }: { fill: boolean }) {
  return (
    <svg
      className={s.mark}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={fill ? 3.2 : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12.5 9.5 18 20 6" />
    </svg>
  );
}

interface Tag {
  key: string;
  tone: "go" | "friends" | "pick" | "warn" | null;
  text: string;
}

/** A row's tags. "On the list" only when no friend has marked the gig, because the row has
 *  one line of tags and who is going is the better use of it. */
function rowTags(e: CalendarEntry): Tag[] {
  const tags: Tag[] = [];
  if (e.going) tags.push({ key: "going", tone: "go", text: "going" });
  if (e.friends.length) {
    tags.push({
      key: "friends",
      tone: "friends",
      // The calendar's hover sentence, from the one place that splits the two marks. A
      // bare list of names under a heading that says "are going" read a friend's save as
      // a plan; `whoIs` says which friend did which.
      text: whoIs(e.friends, { past: e.past }),
    });
  } else if (e.picked) {
    tags.push({ key: "picked", tone: "pick", text: "on the list" });
  }
  if (e.lifecycle !== "scheduled")
    tags.push({ key: "life", tone: "warn", text: e.lifecycle });
  if (e.otherDates > 0)
    tags.push({ key: "dates", tone: null, text: `+${e.otherDates}` });
  return tags;
}

interface RowProps {
  e: CalendarEntry;
  onOpen(id: string): void;
  onSave(e: CalendarEntry): void;
  onGoing(e: CalendarEntry): void;
  /** Whose heart it is. A filled one means "I saved this", and for the editor it also
   *  covers the list they keep — see `onList`. */
  me: Me | null;
  /** Unfollow the act. Absent where following is not offered. */
  onStar?: ((artistId: number, on: boolean) => void) | undefined;
}

function BriefRow({ e, me, onOpen, onSave, onGoing, onStar }: RowProps) {
  const on = mine(e, me);
  const held = heldByStar(e);
  const className = [
    s.lrow,
    e.going ? s.going : on ? s.picked : "",
    e.past ? s.past : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    // `data-id` and `data-lifecycle` are the pair the Python's Done reads back off the DOM to
    // work out which rows nobody answered for. Kept, though this port reads its own state
    // rather than the document — they are the handle every other pass over the page uses.
    <div
      className={className}
      data-id={e.id}
      data-lifecycle={e.lifecycle}
      onClick={ev => openedTheRow(ev) && onOpen(e.id)}
    >
      {/*
       * The star *and* the heart, not one or the other.
       *
       * They were mutually exclusive: a gig held on the list by an act you follow drew the
       * star in the heart's place, so the one thing you could not do to it was decide about
       * it. Which is how a followed act's gig became impossible to take off the brief — the
       * only control offered was about the act, and the row is about the night.
       *
       * They answer different questions and both are worth asking here: the star says why
       * this is on the list and unfollows the act, the heart says what you think of this
       * particular night.
       */}
      {/*
       * The three controls in one element, which on the desktop is `display:contents` and on
       * a phone is the stacked rail — the same trick `.lmeta` below uses, and for the same
       * reason: one row template reads both layouts.
       */}
      <span className={s.marks}>
      {held ? (
        <button
          type="button"
          className={`${s.mini} ${s.starmark}`}
          aria-pressed
          aria-label={`On the list because you follow ${held.name}. Unfollow them`}
          title={`On the list because you follow ${held.name}`}
          onClick={(ev) => {
            ev.stopPropagation();
            onStar?.(held.id, false);
          }}
        >
          <Star fill className={s.mark} />
        </button>
      ) : (
        // The cell a star would have taken, kept so every row has the same number of them.
        // Without it a followed row had one more child than the grid had tracks, and every
        // cell after the star shifted right: the name landed under the venues, the venue
        // under the prices, and the last one ran off the end of the row.
        <span className={s.nostar} aria-hidden="true" />
      )}
      <button
        type="button"
        className={`${s.mini} ${s.heartmark}`}
        aria-pressed={on}
        title={on ? "Curated" : "Add to Curated"}
        onClick={(ev) => {
          ev.stopPropagation();
          onSave(e);
        }}
      >
        <Bookmark fill={on} className={s.mark} />
      </button>
      <button
        type="button"
        className={`${s.mini} ${s.goingmark}`}
        aria-pressed={e.going}
        // The one control the Python ships without a title. Restored: its only content is an
        // `aria-hidden` glyph, so without this the button has no accessible name at all.
        title={e.going ? "You're going" : "Mark as going"}
        onClick={(ev) => {
          ev.stopPropagation();
          onGoing(e);
        }}
      >
        <Tick fill={e.going} />
      </button>
      </span>
      <span className={s.lname} title={e.title}>
        {e.festival ? (
          <span className={s.fest}>{short(e.festival, 24)}</span>
        ) : null}
        {short(e.title, 66)}
      </span>
      <span className={s.lmeta}>
        <span className={s.ltime}>{e.time}</span>
        {/* A span, not the Python's button: filtering the calendar to a venue is the
            calendar's job and this component was given no `onVenue`. */}
        <span className={s.lvenue} title={e.venue}>
          {e.venue}
        </span>
        <span className={s.ltags}>
          {rowTags(e).map((tag, i) => (
            <Fragment key={tag.key}>
              {i > 0 ? " " : null}
              <span className={tag.tone ? `${s.tag} ${s[tag.tone]}` : s.tag}>
                {/*
                 * The friends' faces, ahead of the words that name them. The words stay
                 * — a face is initials for most accounts, and "TB" is not a name — and
                 * carry the count of anyone not drawn in their own "and N more", so the
                 * stack draws no `+N` of its own: two numbers for one fact, side by
                 * side, is what the compact row was cleared of. The row is the tap
                 * target here; the stack is a second way to the same sheet.
                 */}
                {tag.key === "friends" ? (
                  <Faces
                    className={s.faces}
                    friends={e.friends}
                    size={16}
                    past={e.past}
                    to={`/e/${e.id}`}
                    plate={false}
                  />
                ) : null}
                <span>{tag.text}</span>
              </span>
            </Fragment>
          ))}
        </span>
        <span className={s.lprice}>{money(e)}</span>
      </span>
    </div>
  );
}

/* ── the day rail ──────────────────────────────────────────────────────────────────── */

interface DayGroup {
  iso: string;
  /** The first gig of the night, which is where the rail's date is read from — every entry
   *  on the night carries the same one, already formatted in London by the server. */
  head: CalendarEntry;
  items: CalendarEntry[];
}

function groupByDay(list: readonly CalendarEntry[]): DayGroup[] {
  const byDay = new Map<string, DayGroup>();
  for (const e of list) {
    const group = byDay.get(e.iso);
    if (group) group.items.push(e);
    else byDay.set(e.iso, { iso: e.iso, head: e, items: [e] });
  }
  return [...byDay.values()];
}

function Days({
  entries,
  me,
  onOpen,
  onSave,
  onGoing,
  onStar,
  style,
}: { entries: CalendarEntry[]; style: Style } & Omit<RowProps, "e">) {
  return (
    <div className={s.days}>
      {groupByDay(entries).map((group) => (
        // No `is-today`. That class is the one rule in the shared day rail that reads
        // `DATA.today`, and this component has no prop carrying it — the server formats every
        // date in Europe/London precisely so the SPA does not keep a second timezone rule,
        // and the browser clock is a different clock from the one the payload was built with.
        <div key={group.iso} className={s.day} data-date={group.iso}>
          <div className={s.rail}>
            <span className={s.dnum}>{group.head.dayNum}</span>
            <span className={s.dmon}>{group.head.monShort}</span>
            {/* Only when it is not this year, which the server decides. A calendar of the
                next few months says nothing about the year and is right not to; a gig in the
                one after has to say it, or "1 JAN" is a date a reader places next week. */}
            {group.head.otherYear ? (
              <span className={s.dyear}>{group.head.otherYear}</span>
            ) : null}
            <span className={s.ddow}>{group.head.dowShort}</span>
          </div>
          <div
            className={`${s.dayitems} ${style === "full" ? s.full : s.compact}`}
          >
            {group.items.map((e) =>
              style === "full" ? (
                /*
                 * The site's own card, not a second one. `ListView` reached the same
                 * conclusion and for the same reason: there were two cards with one job and
                 * only one of them was being looked at. The marks come from context, so the
                 * briefing's own heart and tick are what the card presses.
                 */
                <div
                  key={e.id}
                  className={s.cardwrap}
                  onClick={ev => openedTheRow(ev) && onOpen(e.id)}
                >
                  <ol
                    className={s.cardlist}
                    data-id={e.id}
                    data-lifecycle={e.lifecycle}
                  >
                    <EventCard event={e} />
                  </ol>
                </div>
              ) : (
                <BriefRow
                  key={e.id}
                  e={e}
                  me={me}
                  onOpen={onOpen}
                  onSave={onSave}
                  onGoing={onGoing}
                  onStar={onStar}
                />
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── the skeleton ──────────────────────────────────────────────────────────────────── */

/** How many placeholder sections, nights and rows a skeleton draws. Enough to fill a screen
 *  with the page's shape; what arrives replaces them whatever its length. */
const SKELETON = { sections: 2, days: 2, items: 3 } as const;

/**
 * Where the sections go while there is no page to draw: before the first `/briefing` lands,
 * and from Done's press until `finish` has an outcome.
 *
 * Built from the page's own containers — `.bsec`, `.day`, `.rail`, `.dayitems` — so the blocks
 * sit where the rows will and the page does not jump when they arrive. Shaped by the style in
 * force, a bar per row or a square per card, for the same reason.
 *
 * Nothing in it is text, a picture or a control. A placeholder that carried any would be a
 * page that says something nobody sent, or a control that answers for a row nobody can see;
 * `aria-hidden` because it is decoration, and the screen's `aria-busy` is what says the page
 * is on its way.
 */
function Skeleton({ style }: { style: Style }) {
  const card = style === "full";
  const count = (n: number) => Array.from({ length: n }, (_, i) => i);
  return (
    <div data-skeleton="" aria-hidden="true">
      {count(SKELETON.sections).map((section) => (
        <div key={section} className={s.bsec}>
          <div className={s.skhead} />
          {count(SKELETON.days).map((day) => (
            <div key={day} className={s.day}>
              <div className={s.rail}>
                <div className={s.skrail} />
              </div>
              <div className={`${s.dayitems} ${card ? s.full : s.compact}`}>
                {count(SKELETON.items).map((item) =>
                  card ? (
                    <div key={item} className={s.skcard}>
                      <div className={s.skart} />
                      <div className={s.skline} />
                      <div className={s.skline} />
                    </div>
                  ) : (
                    <div key={item} className={s.skrow} />
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** One press's patch to the page: a row it is about comes back changed, any other unchanged. */
type Change = (e: CalendarEntry) => CalendarEntry;

/** A press made on the page, kept until a page arrives that is known to show it. */
interface Press {
  change: Change;
  /** Answers for a gig, so Done counts it as decided. A follow does not. */
  decides: boolean;
  /** The value of `heard` when its answer arrived; null while the write is out. */
  answered: number | null;
  /** Already counted by a `/briefed`, so a later page's Done does not count it again. */
  reported: boolean;
}

/** A `/briefing` fetch, as it was sent: `heard` at that moment, and its place in send order. */
interface Asked {
  heard: number;
  number: number;
}

/** `page` with each change applied to every row, in order. */
function redraw(page: BriefingResponse, changes: readonly Change[]): BriefingResponse {
  return {
    ...page,
    sections: page.sections.map((section) => ({
      ...section,
      entries: section.entries.map((e) => changes.reduce((row, change) => change(row), e)),
    })),
  };
}

/* ── the briefing ──────────────────────────────────────────────────────────────────── */

export interface BriefingProps {
  /**
   * The signed-in viewer. Required, because a signed-out reader must never get here — and
   * read, so that a change of account is a fresh queue rather than the last person's.
   */
  me: Me;
  /** Back to the calendar. "Not now", "Leave it", and what a worked-through queue ends on. */
  onClose(): void;
  /** Open the event sheet the calendar owns. */
  onOpen(id: string): void;
}

export function Briefing({ me, onClose, onOpen }: BriefingProps) {
  // The same count the bar's badge reads, from the same place, so the two cannot disagree
  // on screen while claiming to be one number.
  const waiting = useWaiting(me ? me.id : null);
  const [per, setPer] = useState(perPage);
  const [style, setStyle] = useState<Style>(briefStyle);
  const [brief, setBrief] = useState<BriefingResponse | null>(null);
  /** One `/briefed` at a time. Two would dismiss the leftovers twice and count them twice. */
  const busy = useRef(false);
  /**
   * The same fact as `busy`, rendered: from the press on Done until `finish` has an outcome.
   *
   * A second reader of it, not a replacement. Two clicks dispatched in one task both run the
   * `finish` of the same committed render, before React has drawn the `disabled` this sets,
   * so only the ref can stop the second. And setting a ref draws nothing, so only this can
   * show the press was heard before the server answers.
   *
   * It hides the page rather than clearing it: `brief` stays the page Done answered for, so
   * a refused `/briefed` puts the same rows back and a second press sends the same body.
   */
  const [pending, setPending] = useState(false);

  /**
   * The page as `/briefing` gave it, and the presses it may not show yet, in the order they
   * were made. What is on screen is always the first with the second applied — `press` below
   * is why, and `take` is which presses a new page still needs.
   *
   * Done's `decided` is counted off the second rather than kept as a number: the presses this
   * page carries that answer for a gig and that no earlier `/briefed` has counted. A refused
   * press has already left the list, so it is not counted.
   */
  const given = useRef<BriefingResponse | null>(null);
  const pressed = useRef<Press[]>([]);
  /** How many presses have had their answer, so far. A fetch notes it as it is sent. */
  const heard = useRef(0);

  /**
   * How many `/briefing` fetches have been sent, and the number of the one whose page is on
   * screen. Each fetch takes the next number as it is sent; `take` drops an answer whose
   * number is below the page it would replace.
   *
   * Several can be out at once — the page sizes' and Done's — and nothing keeps their answers in
   * the order they were asked. `take` prunes the press list against the page it takes, so
   * taking the newer page and then the older one draws the older one with the presses only
   * the newer showed already gone: a heart answered between the two sends can be missing
   * from the older page with nothing left to put it back, so the row reads as unanswered and
   * the next Done sends it in `dismiss`, deleting the save.
   *
   * Ordered by when each was sent, not when it answered or what it carries, because sending
   * is what `take`'s guarantee is about: every press answered before a fetch was sent is in
   * its page. A fetch sent later was sent after every press the earlier one could count on,
   * so dropping the earlier answer drops nothing the later one lacks.
   */
  const sent = useRef(0);
  const onScreen = useRef(0);
  /**
   * The opening, timed until its first page is taken. On a ref, so a second run of the
   * effect below before that page lands settles the same action rather than none.
   */
  const opening = useRef<Settle | null>(null);

  // Held on a ref so a parent that rebuilds its callbacks on every render cannot re-fire
  // the fetch below. The effect's real dependencies are the page size and the account.
  const leave = useRef(onClose);
  useEffect(() => {
    leave.current = onClose;
  }, [onClose]);

  /**
   * Note `heard` and take the next number, for a request about to leave that will answer
   * with a page. Called just before it is sent, so neither can be moved by anything that
   * happens while it is out: a fetch through `ask`, and Done's `/briefed`, whose answer
   * carries the next page.
   */
  const numbered = useCallback((): Asked => ({ heard: heard.current, number: ++sent.current }), []);

  /**
   * Send a `/briefing` fetch, numbered as it goes. Synchronous, handing back the request's
   * own promise, so numbering a fetch puts no extra step between its answer arriving and the
   * page being taken.
   */
  const ask = useCallback((size: number) => {
    const asked = numbered();
    return { asked, answer: getBriefing(size) };
  }, [numbered]);

  /** Whether a page newer than this fetch's is already on screen. */
  const outdated = useCallback((asked: Asked) => asked.number < onScreen.current, []);

  /**
   * A new page, fetched when `heard` stood at `asked.heard`. Dropped if a fetch sent after
   * it has already been taken — `sent` above is why.
   *
   * The server computes a page with whatever writes it had taken by then, and which those
   * were is known here only by when their answers came back. A press answered before the
   * fetch was sent is in the page. One still out when it was sent may have been taken before
   * the page was computed or after it — a heart pressed while a new page size is loading —
   * and a page that does not show it would leave the row looking unanswered, so Done would
   * say no to a gig just said yes to. So the presses from the first one not answered by
   * then are applied to the new page too, in order. The ones after it go too, answered or
   * not, because a later press on the same row has to land on top of it. Applying a press
   * to a row that already shows it changes nothing: each change sets the fields to what the
   * press asked for, so twice is the same as once.
   */
  const take = useCallback((next: BriefingResponse, asked: Asked) => {
    if (outdated(asked)) return;
    onScreen.current = asked.number;
    const from = pressed.current.findIndex(
      (p) => p.answered === null || p.answered > asked.heard,
    );
    const still = from < 0 ? [] : pressed.current.slice(from);
    given.current = next;
    pressed.current = still;
    setBrief(still.length ? redraw(next, still.map((p) => p.change)) : next);
    // Top of the queue, not wherever the last page left the scroll.
    window.scrollTo(0, 0);
  }, [outdated]);

  /**
   * The fetch. Re-run when the page size changes rather than re-slicing what is already
   * here: the page you are on is a server-side window over a pile of two thousand, and the
   * browser only ever had the twenty-four.
   */
  useEffect(() => {
    let live = true;
    // Only the first page is the briefing opening; a later fetch is a new page size.
    if (!sent.current) opening.current = act("brief");
    void (async () => {
      try {
        const { asked, answer } = ask(per);
        const next = await answer;
        if (live) {
          take(next, asked);
          opening.current?.(true);
          opening.current = null;
        }
      } catch (error) {
        if (!live) return;
        opening.current?.(false);
        opening.current = null;
        if (error instanceof NeedsSignIn) {
          window.location.href = `/login?next=${encodeURIComponent(error.next)}`;
          return;
        }
        // The Python's briefing simply never opens when the fetch fails and the reader is
        // left on the calendar. Same outcome, said explicitly, because by here the screen is
        // already mounted and the alternative is a blank page.
        leave.current();
      }
    })();
    return () => {
      live = false;
    };
    // `me.id`, not `me`: a different account is a different queue, and an object rebuilt by
    // a parent's render is not a different account.
  }, [per, me.id, ask, take]);

  /**
   * A write, guarded the way the Python's `post` guards every one of them: a 401 asks for a
   * sign-in and carries `next` so it brings you back to the row you were about to answer;
   * anything else is said on screen rather than failing silently. `attempt` is that guard
   * for every write on the site; this page had its own, with a `window.alert`, before it did.
   */
  const write = attempt;

  /**
   * A yes on the briefing page redraws the briefing, whose rows come from `/briefing` rather
   * than from the calendar's list — repainting from that list would drop them, because a
   * briefing row is routinely an event the calendar's snapshot has never heard of.
   *
   * Patched in place and NOT re-fetched, which is the Python's `applyToBriefing` and is
   * load-bearing: hearting a row is what takes it out of the undecided pile, so a re-fetch
   * would make the row vanish from under the cursor that just answered it. The page is meant
   * to keep every row it showed until Done, because Done is what says no to the rest.
   *
   * Patch first, write second, and undo the press if the write is refused — the calendar's
   * `decide`, for the same reason: writing first cost the whole round trip before the mark
   * moved, which reads as the control not working.
   *
   * Undone by replaying rather than by putting back what the row held before the press. The
   * page on screen is always the page `/briefing` gave plus the presses `take` kept, in the
   * order pressed; a refusal drops that one press from the list and draws the page again from
   * what was given. Putting the old row back instead erases whatever was pressed on the same
   * row while the refused write was out — a heart, then a tick that the server took, then the
   * heart refused, and the tick went out with it. Negating the pressed field is no better:
   * going sets `liked` as well, so the refused heart's `liked` is the tick's to keep.
   *
   * Every write that patches the page goes through here, the star included, because a press
   * the list does not know about would be erased by the next refusal's redraw.
   *
   * The redraw is computed when the refusal arrives and handed to the queue as a value, so a
   * press made after it is applied on top of it rather than replayed into it twice. It holds
   * on a page fetched while the write was out as much as on the one it was pressed on: `take`
   * carried the press onto that page because it was still out, so the refusal takes it back
   * off, which leaves the row as the server sent it.
   */
  const press = useCallback(
    async (send: () => Promise<unknown>, change: Change, decides: boolean): Promise<boolean> => {
      const made: Press = { change, decides, answered: null, reported: false };
      pressed.current.push(made);
      setBrief((prev) => (prev ? redraw(prev, [change]) : prev));
      const took = await write(send);
      heard.current += 1;
      made.answered = heard.current;
      if (took) return true;
      // Refused, or a sign-in. The sentence on screen is `attempt`'s.
      pressed.current = pressed.current.filter((p) => p !== made);
      if (given.current === null) return false;
      const page = redraw(
        given.current,
        pressed.current.map((p) => p.change),
      );
      setBrief((prev) => (prev ? { ...prev, sections: page.sections } : prev));
      return false;
    },
    [write],
  );

  /** A press that answers for a gig, which Done counts as decided unless it is refused. */
  const decide = useCallback(
    (send: () => Promise<unknown>, change: Change): Promise<boolean> => press(send, change, true),
    [press],
  );

  const decideSave = useCallback(
    async (e: CalendarEntry) => {
      // `mine`, not `onList` — the same predicate the mark is drawn from, so pressing a full
      // one always removes and pressing an empty one always adds. They were two different
      // questions and the answer disagreed on every star-held row.
      const saved = !mine(e, me);
      const took = await decide(
        () => api.save(e.id, saved),
        // What `save_event` will have written: the editor's save sets `curation_status` too.
        // `picked` keeps the star's half, which this press does not touch and cannot.
        (row) =>
          row.id === e.id
            ? {
                ...row,
                liked: saved,
                status: saved ? "added" : "pending",
                picked: saved || Boolean(row.starred),
              }
            : row,
      );
      // The bar's badge counts what is still to decide, and this just changed it. It has
      // no other way to find out: it is above every route and this is inside one. Only once
      // the server has said yes, because until then the count has not moved.
      if (took) waitingChanged();
    },
    // `me` decides what `mine` means, and leaving it out is how a stale editor flag would
    // make every press the wrong way round.
    [me, decide],
  );

  const decideGoing = useCallback(
    async (e: CalendarEntry) => {
      const going = !e.going;
      // Going implies liking. The calendar's default view is what you like, and a gig you
      // hold a ticket for dropping off the first screen you land on is the bug this avoids.
      const took = await decide(
        () => api.going(e.id, going),
        (row) => (row.id === e.id ? { ...row, going, liked: going || row.liked } : row),
      );
      if (took) waitingChanged();
    },
    [decide],
  );

  function choosePer(size: number): void {
    rememberPerPage(size);
    setPer(size);
  }

  /**
   * Done. Silence is a no: whatever is still on the page gets dismissed — but only what was
   * *rendered*. Rows the cap never showed are neither a yes nor a no, and the two watermarks
   * echoed back are what keep them waiting.
   *
   * The rows, their ids and the watermarks are all read off `brief`, and nothing else. A click
   * runs the `finish` of the last render React committed, so its `brief` is the page in the
   * document when Done was pressed. `take` moves its refs at once but `brief` only when React
   * renders, and a page that arrives outside an event is rendered in a later task, so a click
   * queued meanwhile can be dispatched in between. Done used to take its ids from a ref `take`
   * had already moved to the new page and its rows from this one: a row only the new page held
   * found no row to say it was answered, so it went into `dismiss`, unseen, with a null
   * `acknowledged` — which the server keeps for good, deleting the reader's save on the event
   * unless they are going to it.
   */
  async function finish(): Promise<void> {
    if (!brief || busy.current) return;
    const done = act("done");
    // Whether `/briefed` took. A refused one is recorded with shown at the frame of its
    // notice, the frame settled is taken at too: S2.10's rule for a refused Done.
    let took = false;
    // Keyed by id, so an event drawn in two sections is answered for once.
    const rows = new Map<string, CalendarEntry>();
    for (const section of brief.sections)
      for (const e of section.entries) rows.set(e.id, e);

    const left: Leftover[] = [...rows.values()]
      .filter((e) => !answered(e, me))
      .map((e) => ({ id: e.id, acknowledged: e.lifecycle }));

    const counted = pressed.current.filter((p) => p.decides && !p.reported);
    const body: BriefedRequest = {
      decided: counted.length + left.length,
      dismiss: left,
      // The size on screen, so the next page comes back in this answer at the size the
      // reader chose, rather than as a second request.
      per,
    };
    // Echoed back verbatim, and omitted rather than sent as null when the briefing carried
    // none — the server reads both the same way, and `exactOptionalPropertyTypes` will not
    // let a null stand in for an absent `IsoUtc`.
    if (brief.coveredThrough) body.coveredThrough = brief.coveredThrough;
    if (brief.toldThrough) body.toldThrough = brief.toldThrough;

    busy.current = true;
    setPending(true);
    // The page gives way to the skeleton, and the next one is drawn from the top: Done sits at
    // the foot of the page, so the press is made from wherever the page ends.
    window.scrollTo(0, 0);
    try {
      // Numbered as it is sent, the way `ask` numbers a fetch, because its answer is a page
      // and `take`'s guarantee is about sending: the server cuts that page after it has every
      // press answered by now, and a page-size fetch sent after this is the later word,
      // whichever of the two answers first.
      const numberedAs = numbered();
      const reply: { body: BriefedResponse | null } = { body: null };
      if (
        !(await write(async () => {
          reply.body = await postBriefed(body);
        }))
      )
        return;
      took = true;
      for (const p of counted) p.reported = true;
      const page = reply.body?.next;
      if (page) {
        // Through `take`, like any page: a press still out when the `POST` left is applied
        // to it, and it is dropped if a page sent later is already on screen. An empty page
        // leaves only when it is not outdated, for the reason the fetch below gives.
        if (page.total) take(page, numberedAs);
        else if (!outdated(numberedAs)) leave.current();
        // After the rows rather than racing them: the count follows as its own request.
        waitingChanged();
        return;
      }
      // A server that answered without the page — one from before it carried one, while a
      // deploy is under way. Everything from here is Done as it was before the answer carried
      // the page.
      //
      // A page of leftovers just became a page of noes, which is the largest the count ever
      // moves in one press — two hundred and thirty of them, and the badge did not budge.
      waitingChanged();
      // Straight on to the next page rather than back to the calendar. The queue is eighty
      // pages; making you re-open it after each one is the difference between a thing you
      // work through and a thing you abandon. Re-fetched, because the browser only ever had
      // this page of it.
      const { asked, answer } = ask(per);
      const next = await answer;
      // An empty answer is dropped with the rest when it is outdated: the newer page on
      // screen is the later word on whether anything is left.
      if (next.total) take(next, asked);
      else if (!outdated(asked)) leave.current();
    } catch (error) {
      if (error instanceof NeedsSignIn) {
        window.location.href = `/login?next=${encodeURIComponent(error.next)}`;
        return;
      }
      leave.current();
    } finally {
      // Every end of a `finish` that raised pending passes here, and nothing else lowers it. A
      // page taken from a size chosen meanwhile goes into `brief` behind the skeleton rather
      // than ending pending, so it is Done's own outcome that ends it, whichever that is.
      busy.current = false;
      setPending(false);
      // Here, after the page that ends it is taken or the notice is up, whichever it was.
      done(took, !took);
    }
  }

  if (brief && !brief.total) {
    return (
      <div className={`${s.screen} ${s.bdone}`}>
        <h2>Nothing to decide</h2>
        <p>Everything since {brief.since ?? "you started"} is dealt with.</p>
        <button type="button" className={s.bact} onClick={onClose}>
          Back to the calendar
        </button>
      </div>
    );
  }

  /*
   * No page to draw: the first has not arrived, or Done has answered for this one and the
   * next is on its way. The header is drawn either way — leaving, the style and the size do
   * not need a page — and the skeleton stands where the sections go.
   */
  const waitingForPage = !brief || pending;
  const more = brief ? brief.pages - 1 : 0;
  /** Every row on the page, which is what a card's mark has to be looked up in. */
  const shown = brief ? brief.sections.flatMap((section) => section.entries) : [];
  /**
   * The pile, or null when nothing has said how big it is yet. Never a `0` standing in for
   * "not known": zero is a real count, the one a finished pile shows.
   */
  const pile = waiting.loaded ? waiting.briefing : brief ? brief.outstanding : null;

  /*
   * Follow or unfollow the act, from a row its gig is on.
   *
   * The row used to offer nothing here but a `role="img"` label explaining why the heart was
   * missing. Both marks are here now, because they answer different questions: this one is
   * about the act and every night they play, the heart beside it is about this night.
   *
   * It takes the flag rather than always unfollowing, because the card reads the same
   * action from context and its star has to be able to go both ways.
   */
  const setFollow = (artistId: number, on: boolean) =>
    void press(
      () => api.star(artistId, on),
      (e) => (e.act?.id === artistId ? { ...e, starred: on } : e),
      false,
    );

  /*
   * The card's marks are the briefing's own.
   *
   * `EventCard` asks context rather than taking callbacks, which is what stopped the three
   * having to be re-wired at every call site — and the other half of that bargain is that a
   * page drawing cards must answer. Answering with `decideSave` and `decideGoing` is what
   * makes the two styles the same queue: a heart pressed on a card counts towards the page
   * exactly as one pressed on a row does.
   */
  const acting: MarksActions = {
    me,
    save: (id, on) => {
      const found = shown.find((e) => e.id === id);
      if (found && Boolean(onList(found, me)) !== on) decideSave(found);
    },
    going: (id, on) => {
      const found = shown.find((e) => e.id === id);
      if (found && Boolean(found.going) !== on) decideGoing(found);
    },
    star: setFollow,
  };

  return (
    <MarksProvider value={acting}>
      <div className={s.screen} aria-busy={waitingForPage ? true : undefined}>
        <div className={s.bhead}>
          <div className={s.btop}>
            {/*
              * The size of the pile, not of this page — and the same number the bar's badge
              * shows, from the same place.
              *
              * It used to be `brief.outstanding`, painted once from the fetch that opened
              * the page. So the badge counted down as you worked and this did not, and the
              * two disagreed on screen at the same moment while claiming to be one fact.
              *
              * `brief.outstanding` until the hook has answered, which is what `loaded` is
              * for: zero is a real count, and without it a finished pile would show the
              * figure the page opened with. Before either has answered, a blank block where
              * the number goes.
              */}
            <h2>
              {pile === null ? (
                <span className={s.skcount} aria-hidden="true" />
              ) : (
                pile.toLocaleString()
              )}{" "}
              left to decide
            </h2>
            {/* The way out, at the top. Leaving was only ever offered at the bottom of the
              page, which on a queue this long means answering for two dozen events to reach
              the button that says you would rather not. */}
            <button type="button" className={s.bact} onClick={onClose}>
              Not now
            </button>
          </div>
          <div className={s.when}>
            {brief ? (
              <>
                Page {brief.page + 1} of {brief.pages}{" "}
                <span className={s.sep}>/</span>{" "}
              </>
            ) : null}
            heart or tick what you want — anything you leave on a page you finish is a no
          </div>
          {/* How much of the pile to put in front of yourself at once. Kept on the device
            rather than the account, because it is a fact about the screen you are reading
            on: twenty-four is a lot on a phone and barely a scroll on a monitor. */}
          {/* Rows or cards. The same kind of preference as the page size and kept the same
            way — on the device, because it is about the screen rather than the account. */}
          <div className={s.bsize}>
            <span>Show as</span>
            {STYLES.map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={key === style}
                onClick={() => {
                  rememberStyle(key);
                  setStyle(key);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className={s.bsize}>
            <span>Per page</span>
            {PAGE_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                aria-pressed={size === per}
                onClick={() => choosePer(size)}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        {/* Kept in the payload's own order: the sections run most-urgent first, and re-sorting
          them by date here would bury a cancellation under a month of suggestions. */}
        {waitingForPage ? (
          <Skeleton style={style} />
        ) : (
          brief.sections.map((section) => (
            <section
              key={section.key}
              className={
                section.key === "upset" ? `${s.bsec} ${s.urgent}` : s.bsec
              }
            >
              <h3>{section.title}</h3>
              {/* A heading is a name. The pile's four sections send no note, because the
                  count and the page are in the header above and a sentence here repeated
                  them; an empty note is no element, not an empty paragraph holding its
                  margin open under the heading. */}
              {section.note ? <p className={s.note}>{section.note}</p> : null}
              <Days
                me={me}
                entries={section.entries}
                onOpen={onOpen}
                onSave={decideSave}
                onGoing={decideGoing}
                onStar={setFollow}
                style={style}
              />
            </section>
          ))
        )}

        {/* No foot before the first page: there is nothing yet for Done to answer for. While
          Done is pending it stays, so the button that was pressed is the one seen working.
          The label does not change, because it is the name the button is found by. */}
        {brief ? (
          <div className={s.bfoot}>
            <span className={s.bnote}>
              {more > 0
                ? `${more} more page${more > 1 ? "s" : ""} after this`
                : "last page"}
            </span>
            <button type="button" className={s.bact} onClick={onClose}>
              Leave it
            </button>
            <button
              type="button"
              className={`${s.bact} ${s.go}`}
              disabled={pending}
              aria-busy={pending ? true : undefined}
              onClick={finish}
            >
              {brief.pages > 1 ? "Done — next page →" : "Done"}
            </button>
          </div>
        ) : null}
      </div>
    </MarksProvider>
  );
}
